import { ResponseAuth, AuthSource } from '../types';
import api, { logoutUser } from './api';
import { API_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import { isTokenExpired, getTokenInfo, clearStoredTokens } from '../utils/tokenUtils';

function encodeToken(phone?: string) {
  const plainText = `phone:${phone}`;
  const secret = 'mySecretKey123';
  const textBytes = Array.from(new TextEncoder().encode(plainText));
  const secretBytes = Array.from(new TextEncoder().encode(secret));
  const xored = textBytes.map((b, i) => b ^ secretBytes[i % secretBytes.length]);
  const base64 = btoa(String.fromCharCode(...xored));
  return base64;
}

const normalizePhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // If the number starts with 8, replace it with 7
  if (digitsOnly.startsWith('8')) {
    return '7' + digitsOnly.slice(1);
  }
  
  // If the number doesn't start with 7, add it
  if (!digitsOnly.startsWith('7')) {
    return '7' + digitsOnly;
  }
  
  return digitsOnly;
}; 

class AuthService {
  async authenticate(phoneNumber: string, source: AuthSource): Promise<ResponseAuth> {
    try {
      const phone = phoneNumber && phoneNumber.replace(/\D/g, '').length >= 10
        ? normalizePhoneNumber(phoneNumber)
        : '0';

      const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, {
        phone: parseInt(phone, 10), // Преобразуем в number для API
        source,
        authToken: encodeToken(phone),
      });

      return response.data as ResponseAuth;
    } catch (error) {
      console.error('Auth error:', error);
      throw error;
    }
  }

  async verify(phoneNumber: string, code: string): Promise<ResponseAuth> {
    try {
      const phone = phoneNumber && phoneNumber.replace(/\D/g, '').length >= 10
        ? normalizePhoneNumber(phoneNumber)
        : '0';

        const response = await api.post(API_ENDPOINTS.AUTH.VERIFY, {
        phone: parseInt(phone, 10), // Преобразуем в number для API
        code,
      });

      return response.data as ResponseAuth;
    } catch (error) {
      console.error('SMS verification error:', error);
      throw error;
    }
  }

  async refreshTokens(accessToken: string, refreshToken: string): Promise<ResponseAuth> {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.REFRESH, {
        accessToken,
        refreshToken,
      });

      return response.data as ResponseAuth;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  // Сохранение токенов в localStorage
  saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    
    // Обновляем заголовок по умолчанию для всех будущих запросов
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  // Получение токенов из localStorage
  getTokens(): { accessToken: string | null; refreshToken: string | null } {
    return {
      accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
      refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
    };
  }

  // Проверка авторизации
  isAuthenticated(): boolean {
    const { accessToken } = this.getTokens();
    return !!accessToken;
  }

  // Выход из системы
  logout(): void {
    logoutUser();
  }

  // Проверка срока действия токена
  isTokenExpired(): boolean {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) return true;

    return isTokenExpired(token);
  }

  // Получение информации о токене
  getTokenInfo() {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) return null;

    return getTokenInfo(token);
  }

  // Очистка токенов
  clearTokens(): void {
    clearStoredTokens();
  }
}

export const authService = new AuthService();
export default authService;
